'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { RiMenuLine } from '@remixicon/react'
import { hamburgerMenuStore } from '../../../shared/Header/Store/hamburgerMenuStore'
import type { SidebarLogo } from './Sidebar'

export interface MobileTopHeaderProps {
  logo: SidebarLogo
  homeHref: string
  profileSlot?: React.ReactNode
  menuLabel?: string
}

/** Logo, profile and the menu button. The only chrome above the page on a phone. */
export const MobileTopHeader: React.FC<MobileTopHeaderProps> = ({
  logo,
  homeHref,
  profileSlot,
  menuLabel = 'Open menu',
}) => {
  const { open } = hamburgerMenuStore()

  return (
    <div className="w-full px-4 py-3.5 bg-white border-b border-zinc-200 flex justify-between items-center relative z-50">
      <Link href={homeHref} className="dashboard-nav-link flex items-center">
        <Image
          className="h-auto object-contain"
          style={{ width: logo.width }}
          src={logo.src}
          alt={logo.alt}
          width={logo.width}
          height={logo.height}
          unoptimized
        />
      </Link>

      <div className="flex justify-start items-center gap-3">
        {profileSlot}
        <button
          type="button"
          onClick={open}
          aria-label={menuLabel}
          // 44px square: the minimum comfortable tap target, and this is the
          // only way to the rest of the navigation on a phone.
          className="w-11 h-11 rounded-[10px] flex justify-center items-center cursor-pointer bg-dashboard-accent-surface text-dashboard-text"
        >
          <RiMenuLine size={20} />
        </button>
      </div>
    </div>
  )
}

/**
 * The page title strip under the top header.
 *
 * The previous co-owner dashboard hid its title bar entirely below 993px, so a
 * phone gave no indication of which screen you were on.
 */
export const MobileSubHeader: React.FC<{ title: string; actions?: React.ReactNode }> = ({
  title,
  actions,
}) => (
  <div className="w-full h-12 px-4 bg-dashboard-canvas border-b border-gray-200 flex justify-between items-center">
    <div className="text-dashboard-text text-xs font-medium leading-tight">{title}</div>
    {actions && <div className="flex justify-start items-center gap-3">{actions}</div>}
  </div>
)
